import React, { useState, useEffect, useCallback, useRef } from 'react';

// Detect India via timezone — IST users see ₹999, everyone else sees $9.99
const _tz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; } })();
const UPGRADE_PRICE = (_tz === 'Asia/Kolkata' || _tz === 'Asia/Calcutta') ? '₹999/mo' : '$9.99/mo';
import { Layout, Button, Drawer, Grid, Tooltip, Switch, Badge, Modal, Input, Spin, Popover, Empty, Tag } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined, UnorderedListOutlined, TeamOutlined,
  MailOutlined, StopOutlined, LogoutOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined, SettingOutlined,
  MenuOutlined, FileTextOutlined, BranchesOutlined, FormOutlined,
  CrownOutlined, ThunderboltOutlined, SafetyOutlined,
  BarChartOutlined, BankOutlined, UserSwitchOutlined,
  ApiOutlined, AuditOutlined, AlertOutlined,
  InboxOutlined, DesktopOutlined, DollarOutlined, LockOutlined,
  BellOutlined, SearchOutlined, CloudOutlined, SafetyCertificateOutlined,
  ApartmentOutlined, RiseOutlined, HistoryOutlined, CustomerServiceOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';

const { Sider, Header, Content } = Layout;
const { useBreakpoint } = Grid;

const NAV_ITEMS = [
  { key: '/',                icon: <DashboardOutlined />,          label: 'Overview' },
  { key: '/campaigns',       icon: <MailOutlined />,               label: 'Campaigns' },
  { key: '/automations',     icon: <BranchesOutlined />,           label: 'Automations', proOnly: true },
  { key: '/lists',           icon: <UnorderedListOutlined />,      label: 'Audiences' },
  { key: '/dynamic-lists',   icon: <ApartmentOutlined />,          label: 'Dynamic List', proOnly: true, indent: true },
  { key: '/subscribers',     icon: <TeamOutlined />,               label: 'Subscribers' },
  { key: '/forms',           icon: <FormOutlined />,               label: 'Signup Forms', proOnly: true },
  { key: '/templates',       icon: <FileTextOutlined />,           label: 'Templates' },
  { key: '/deliverability',  icon: <SafetyCertificateOutlined />,  label: 'Deliverability' },
  { key: '/suppression',     icon: <StopOutlined />,               label: 'Suppression' },
  { key: '/activity',        icon: <HistoryOutlined />,            label: 'Activity Log' },
  { key: '/support',         icon: <CustomerServiceOutlined />,    label: 'Support', proOnly: true },
];

const ADMIN_NAV_ITEMS = [
  { key: '/admin',             icon: <BarChartOutlined />,  label: 'Platform Overview' },
  { key: '/admin/engagement',  icon: <ThunderboltOutlined />, label: 'Engagement' },
  { key: '/admin/orgs',        icon: <BankOutlined />,      label: 'Organisations' },
  { key: '/admin/users',       icon: <TeamOutlined />,      label: 'All Users' },
  { key: '/admin/campaigns',   icon: <MailOutlined />,      label: 'All Campaigns' },
  { key: '/admin/email-logs',  icon: <InboxOutlined />,     label: 'Email Logs' },
  { key: '/admin/bounces',     icon: <AlertOutlined />,     label: 'Bounce Report' },
  { key: '/admin/suppression', icon: <StopOutlined />,      label: 'Suppression' },
  { key: '/admin/system',      icon: <DesktopOutlined />,   label: 'System Health' },
  { key: '/admin/audit',       icon: <AuditOutlined />,     label: 'Audit Log' },
  { key: '/admin/billing',     icon: <DollarOutlined />,    label: 'Billing Overview' },
  { key: '/admin/revenue',     icon: <RiseOutlined />,      label: 'Revenue Report' },
  { key: '/admin/support',    icon: <CustomerServiceOutlined />, label: 'Support Queue' },
];

const PAGE_TITLES = {
  '/':                    'Overview',
  '/lists':               'Audiences',
  '/subscribers':         'Subscribers',
  '/campaigns':           'Campaigns',
  '/campaigns/create':    'New Campaign',
  '/suppression':         'Suppression',
  '/settings':            'Settings',
  '/templates':           'Templates',
  '/dynamic-lists':       'Dynamic List',
  '/billing':             'Plan & Billing',
  '/deliverability':      'Deliverability',
  '/activity':            'Account Activity',
  '/admin':               'Platform Overview',
  '/admin/engagement':    'Engagement',
  '/admin/orgs':          'All Organisations',
  '/admin/users':         'All Users',
  '/admin/campaigns':     'All Campaigns',
  '/admin/email-logs':    'Email Logs',
  '/admin/bounces':       'Bounce Report',
  '/admin/suppression':   'Suppression Admin',
  '/admin/system':        'System Health',
  '/admin/audit':         'Audit Log',
  '/admin/billing':       'Billing Overview',
  '/admin/revenue':       'Revenue Report',
  '/support':             'Support',
  '/admin/support':       'Support Queue',
};

/* ── Decode JWT ── */
const getSidebarInfo = () => {
  try {
    const t = localStorage.getItem('token');
    if (!t) return { plan: 'free', is_super_admin: false, org_name: '', email: '', name: '' };
    const p = t.split('.')[1];
    const padded = p + '='.repeat((4 - p.length % 4) % 4);
    const payload = JSON.parse(atob(padded));
    return {
      plan:           payload?.plan           || 'free',
      is_super_admin: payload?.is_super_admin || false,
      org_name:       payload?.org_name       || '',
      email:          payload?.email          || '',
      name:           payload?.name           || '',
    };
  } catch { return { plan: 'free', is_super_admin: false, org_name: '', email: '', name: '' }; }
};

/* ── SES Quota Card (sidebar) ── */
const SesQuotaCard = ({ adminMode, isDark }) => {
  const [quota, setQuota] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    axios.get('/api/settings/ses/quota', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setQuota(r.data))
      .catch(() => {});
  }, []);

  if (!quota || !quota.configured || !quota.fromEmail) return null;

  const sent   = quota.sentLast24Hours || 0;
  const limit  = quota.max24HourSend   || 1;
  const pct    = Math.min(1, sent / limit);
  const pctFmt = Math.round(pct * 100);
  const color  = pct >= 0.9 ? 'var(--danger)' : pct >= 0.6 ? 'var(--warn)' : 'var(--ok)';

  const cardBg     = isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-2)';
  const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border)';
  const textPrimary = isDark ? 'rgba(255,255,255,0.85)' : 'var(--text)';
  const textMuted   = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-3)';
  const trackBg    = isDark ? 'rgba(255,255,255,0.1)' : 'var(--border)';

  return (
    <div style={{
      margin: '4px 10px 8px',
      padding: '10px 12px',
      background: cardBg,
      border: `1px solid ${cardBorder}`,
      borderRadius: 'var(--r-md)',
      flexShrink: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <CloudOutlined style={{ color: 'var(--brand-primary)', fontSize: 12 }} />
        <span style={{ fontSize: 11.5, fontWeight: 600, color: textMuted }}>SES Daily Quota</span>
      </div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 17, fontWeight: 600, color: textPrimary, letterSpacing: '-0.02em' }}>
        {sent.toLocaleString()}
        <span style={{ color: textMuted, fontWeight: 400, fontSize: 12 }}>/{(limit / 1000).toFixed(0)}k</span>
      </div>
      <div style={{ height: 4, background: trackBg, borderRadius: 2, marginTop: 6, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pctFmt}%`, background: color, borderRadius: 2, transition: 'width 0.3s' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11, color: textMuted }}>
        <span>{pctFmt}% used</span>
        <span>{Math.max(0, limit - sent).toLocaleString()} left</span>
      </div>
    </div>
  );
};

/* ── Sidebar ── */
const SidebarContent = ({
  collapsed, isMobile, activeKey,
  onNavigate, onCollapse, onLogout,
  adminMode, onToggleAdminMode,
  isDark,
}) => {
  const { plan: jwtPlan, is_super_admin } = getSidebarInfo();
  // The JWT can be stale right after an upgrade — fetch the live plan and prefer it.
  const [livePlan, setLivePlan] = useState(null);
  useEffect(() => {
    axios.get('/api/billing/status', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => { if (r.data?.plan) setLivePlan(r.data.plan); })
      .catch(() => {});
  }, []);
  const plan = livePlan || jwtPlan;
  const userName  = localStorage.getItem('userName')  || '';
  const userEmail = localStorage.getItem('userEmail') || '';

  const initials = userName
    ? userName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : userEmail?.[0]?.toUpperCase() || 'U';

  // ── Design tokens per mode ──────────────────────────
  const sideBg     = isDark ? '#1e1b4b' : 'var(--surface)';
  const sideBorder = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border)';
  const navText    = isDark ? 'rgba(255,255,255,0.6)' : 'var(--text-2)';
  // Admin nav: red active accent; user nav: brand-primary
  const activeText = adminMode ? '#dc2626' : isDark ? '#c7d2fe' : 'var(--brand-primary)';
  const activeBg   = adminMode ? 'rgba(239,68,68,0.08)' : isDark ? 'rgba(255,255,255,0.12)' : 'var(--brand-tint)';
  const hoverBg    = adminMode ? 'rgba(239,68,68,0.06)' : isDark ? 'rgba(255,255,255,0.06)' : 'var(--brand-tint)';

  const logoIconBg = adminMode
    ? 'linear-gradient(135deg, #dc2626, #b91c1c)'
    : isDark
      ? 'linear-gradient(135deg, #2e6bf5, #0a40d9)'
      : 'linear-gradient(135deg, var(--brand-primary), var(--brand-ink))';

  const navItems = adminMode ? ADMIN_NAV_ITEMS : NAV_ITEMS;

  const proLabel = (label) => (
    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
      <span>{label}</span>
      <LockOutlined style={{ fontSize: 10, opacity: 0.5, marginLeft: 4 }} />
    </span>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: sideBg, transition: 'background 0.2s' }}>

      {/* Logo */}
      <Tooltip title={collapsed && !isMobile ? 'Expand sidebar' : ''} placement="right">
      <div
        style={{
          padding: collapsed && !isMobile ? '16px 0' : '16px 16px',
          borderBottom: `1px solid ${sideBorder}`,
          display: 'flex', alignItems: 'center',
          justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
          gap: 10, cursor: 'pointer', flexShrink: 0,
        }}
        onClick={() => collapsed && !isMobile ? onCollapse() : onNavigate(adminMode ? '/admin' : '/')}
      >
        {adminMode ? (
          <div style={{
            width: 32, height: 32,
            background: logoIconBg,
            borderRadius: 9, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 10px rgba(220,38,38,0.4)',
          }}>
            <SafetyOutlined style={{ color: '#fff', fontSize: 15 }} />
          </div>
        ) : (
          <img src="/icon.png" alt="" width={32} height={32}
            style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, display: 'block', boxShadow: '0 4px 10px rgba(10,64,217,0.3)' }} />
        )}
        {(!collapsed || isMobile) && (
          <div>
            <div style={{ fontWeight: 700, fontSize: 14.5, letterSpacing: '-0.3px', lineHeight: 1.2,
              color: isDark ? '#ffffff' : 'var(--text)' }}>
              {adminMode ? 'Admin Panel' : 'SES Mailbox'}
            </div>
            {adminMode && (
              <div style={{ fontSize: 10, color: '#dc2626', fontWeight: 600,
                letterSpacing: '0.05em', textTransform: 'uppercase', lineHeight: 1.2 }}>
                Super Admin
              </div>
            )}
          </div>
        )}
      </div>
      </Tooltip>

      {/* Org switcher (user mode, not collapsed) */}
      {(!collapsed || isMobile) && !adminMode && (
        <button
          onClick={() => onNavigate('/settings')}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            margin: '10px 10px 2px', padding: '8px 10px',
            background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-2)',
            border: `1px solid ${sideBorder}`,
            borderRadius: 'var(--r-md)',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <div style={{
            width: 26, height: 26, borderRadius: 7,
            background: isDark ? '#0a40d9' : 'var(--brand-ink)',
            color: '#fff', display: 'grid', placeItems: 'center',
            fontWeight: 700, fontSize: 11, flexShrink: 0,
          }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              color: isDark ? '#fff' : 'var(--text)' }}>
              {localStorage.getItem('userName') || 'My Workspace'}
            </div>
            <div style={{ fontSize: 11, lineHeight: 1.2, color: isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-3)' }}>
              {plan === 'pro' ? 'Pro' : 'Free'} plan
            </div>
          </div>
          <SettingOutlined style={{ fontSize: 13, color: navText, flexShrink: 0 }} />
        </button>
      )}

      {/* Nav items */}
      <nav style={{ padding: '6px 8px', flex: 1, overflowY: 'auto' }}>
        {navItems.map(item => {
          const isActive = activeKey === item.key;
          const locked   = item.proOnly && plan !== 'pro';
          return (
            <Tooltip key={item.key} title={collapsed && !isMobile ? item.label : ''} placement="right">
              <div
                onClick={() => onNavigate(item.key)}
                style={{
                  display: 'flex', alignItems: 'center',
                  gap: 10,
                  padding: collapsed && !isMobile ? '8px 0' : '7px 10px',
                  paddingLeft: !collapsed && !isMobile && item.indent ? 26 : undefined,
                  margin: '1px 0',
                  borderRadius: 'var(--r-md)',
                  fontSize: item.indent ? 12.5 : 13, fontWeight: 500,
                  cursor: 'pointer',
                  justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                  color:      isActive ? activeText : locked ? 'var(--text-3)' : navText,
                  background: isActive ? activeBg  : 'transparent',
                  transition: 'background 0.15s, color 0.15s',
                  userSelect: 'none',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = hoverBg; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontSize: 15, display: 'flex', alignItems: 'center', flexShrink: 0,
                  color: isActive ? activeText : locked ? 'var(--text-3)' : navText }}>
                  {item.icon}
                </span>
                {(!collapsed || isMobile) && (
                  <>
                    <span style={{ flex: 1 }}>
                      {locked ? proLabel(item.label) : item.label}
                    </span>
                    {item.tag && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 999,
                        background: 'var(--brand-tint)', color: 'var(--brand-primary)',
                      }}>
                        {item.tag}
                      </span>
                    )}
                  </>
                )}
              </div>
            </Tooltip>
          );
        })}
      </nav>

      {/* Admin mode toggle — super admins only */}
      {getSidebarInfo().is_super_admin && (
        <div style={{
          margin: '0 8px 6px',
          padding: collapsed && !isMobile ? '10px 0' : '9px 12px',
          borderRadius: 'var(--r-md)',
          border: adminMode ? '1px solid rgba(239,68,68,0.4)' : '1px solid rgba(239,68,68,0.3)',
          background: adminMode ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.08)',
          display: 'flex', alignItems: 'center',
          justifyContent: collapsed && !isMobile ? 'center' : 'space-between',
          gap: 8, flexShrink: 0,
          cursor: collapsed && !isMobile ? 'pointer' : 'default',
        }}
          onClick={collapsed && !isMobile ? onToggleAdminMode : undefined}
        >
          {collapsed && !isMobile ? (
            <Tooltip title={adminMode ? 'Switch to User View' : 'Admin Mode'} placement="right">
              <UserSwitchOutlined style={{ color: '#ef4444', fontSize: 16 }} />
            </Tooltip>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <UserSwitchOutlined style={{ color: '#ef4444', fontSize: 13, flexShrink: 0 }} />
                <span style={{ color: adminMode ? '#fca5a5' : '#f87171', fontWeight: 600, fontSize: 12 }}>
                  Admin Mode
                </span>
              </div>
              <Switch
                size="small"
                checked={adminMode}
                onChange={onToggleAdminMode}
                style={{ backgroundColor: adminMode ? '#dc2626' : undefined, flexShrink: 0 }}
              />
            </>
          )}
        </div>
      )}

      {/* Plan nudge — free users, user mode, not collapsed */}
      {(!collapsed || isMobile) && !adminMode && plan !== 'pro' && !getSidebarInfo().is_super_admin && (
        <div
          onClick={() => onNavigate('/billing')}
          style={{
            margin: '0 10px 6px', padding: '10px 12px',
            borderRadius: 'var(--r-md)',
            background: isDark
              ? 'linear-gradient(135deg,rgba(10,64,217,0.25),rgba(46,107,245,0.25))'
              : 'linear-gradient(135deg,var(--brand-tint),oklch(0.94 0.06 263))',
            border: isDark ? '1px solid rgba(10,64,217,0.3)' : '1px solid var(--brand-tint-2)',
            cursor: 'pointer', flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
            <ThunderboltOutlined style={{ color: 'var(--brand-primary)', fontSize: 12 }} />
            <span style={{ color: 'var(--brand-primary)', fontWeight: 600, fontSize: 12 }}>Upgrade to Pro</span>
          </div>
          <span style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-3)', fontSize: 11 }}>
            Automations, forms &amp; more — {UPGRADE_PRICE}
          </span>
        </div>
      )}

      {/* Pro badge when collapsed */}
      {collapsed && !isMobile && plan === 'pro' && !adminMode && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
          <Tooltip title="Pro Plan" placement="right">
            <CrownOutlined style={{ color: '#fbbf24', fontSize: 16 }} />
          </Tooltip>
        </div>
      )}

      {/* SES Quota Card */}
      {(!collapsed || isMobile) && (
        <SesQuotaCard adminMode={adminMode} isDark={isDark} />
      )}

      {/* User profile + bottom controls */}
      <div style={{ borderTop: `1px solid ${sideBorder}`, padding: '8px 10px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        {collapsed && !isMobile ? (
          <Tooltip title="Settings" placement="right">
            <div
              style={{
                width: 30, height: 30, borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-ink))',
                color: '#fff', display: 'grid', placeItems: 'center',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}
              onClick={() => onNavigate('/settings')}
            >
              {localStorage.getItem('userName')?.[0]?.toUpperCase() ||
               localStorage.getItem('userEmail')?.[0]?.toUpperCase() || 'U'}
            </div>
          </Tooltip>
        ) : (
          <>
            <div
              style={{
                width: 30, height: 30, borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-ink))',
                color: '#fff', display: 'grid', placeItems: 'center',
                fontSize: 12, fontWeight: 700, flexShrink: 0, cursor: 'pointer',
              }}
              onClick={() => onNavigate('/settings')}
            >
              {initials}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                color: isDark ? '#fff' : 'var(--text)' }}>
                {localStorage.getItem('userName') || 'User'}
              </div>
              <div style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-3)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {localStorage.getItem('userEmail') || ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
              {!isMobile && (
                <Tooltip title={collapsed ? 'Expand' : 'Collapse'} placement="top">
                  <Button
                    type="text" size="small"
                    icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                    onClick={onCollapse}
                    style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-3)' }}
                  />
                </Tooltip>
              )}
              <Tooltip title="Logout" placement="top">
                <Button
                  type="text" size="small"
                  icon={<LogoutOutlined />}
                  onClick={onLogout}
                  style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-3)' }}
                />
              </Tooltip>
            </div>
          </>
        )}
        {/* Expand + Logout when collapsed */}
        {collapsed && !isMobile && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: '100%' }}>
            <Tooltip title="Expand sidebar" placement="right">
              <Button
                type="text" size="small"
                icon={<MenuUnfoldOutlined />}
                onClick={onCollapse}
                style={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'var(--text-2)' }}
              />
            </Tooltip>
            <Tooltip title="Logout" placement="right">
              <Button
                type="text" size="small"
                icon={<LogoutOutlined />}
                onClick={onLogout}
                style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-3)' }}
              />
            </Tooltip>
          </div>
        )}
      </div>

    </div>
  );
};

/* ── Main layout ── */
const AppLayout = ({ children }) => {
  const [collapsed,  setCollapsed]  = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [adminMode,  setAdminMode]  = useState(() => localStorage.getItem('adminMode') === 'true');
  const screens  = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();

  const isDark = theme.mode === 'dark'; // admin mode no longer forces dark

  const headerBg     = isDark ? '#1a1a2e' : 'var(--surface)';
  const headerBorder = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border)';
  const headerTitle  = isDark ? '#f0f0f0' : 'var(--text)';
  const contentBg    = isDark ? '#141414' : 'var(--bg)';

  const SIDEBAR_WIDTH    = 232;
  const COLLAPSED_WIDTH  = 70;

  /* ── Search ── */
  const [searchOpen,    setSearchOpen]    = useState(false);
  const [searchQuery,   setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState({ campaigns: [], subscribers: [], lists: [] });
  const [searchLoading, setSearchLoading] = useState(false);
  const searchInputRef = useRef(null);

  // ⌘K / Ctrl+K global shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(o => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Focus input when modal opens
  useEffect(() => {
    if (searchOpen) {
      setSearchQuery('');
      setSearchResults({ campaigns: [], subscribers: [], lists: [] });
      setTimeout(() => searchInputRef.current?.focus(), 80);
    }
  }, [searchOpen]);

  // Debounced search
  useEffect(() => {
    if (!searchOpen || !searchQuery.trim()) {
      setSearchResults({ campaigns: [], subscribers: [], lists: [] });
      return;
    }
    const t = setTimeout(async () => {
      setSearchLoading(true);
      const h = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const q = encodeURIComponent(searchQuery.trim());
      const [campRes, subRes, listRes] = await Promise.allSettled([
        axios.get(`/api/campaigns?search=${q}&limit=6`, { headers: h }),
        axios.get(`/api/subscribers?search=${q}&limit=6`, { headers: h }),
        axios.get('/api/lists', { headers: h }),
      ]);
      const camps = campRes.status === 'fulfilled'
        ? (Array.isArray(campRes.value.data) ? campRes.value.data : campRes.value.data?.data ?? [])
        : [];
      const subs  = subRes.status  === 'fulfilled' ? (subRes.value.data?.data ?? []) : [];
      const rawLists = listRes.status === 'fulfilled' ? (listRes.value.data || []) : [];
      const q2 = searchQuery.trim().toLowerCase();
      const lists = rawLists.filter(l => l.name?.toLowerCase().includes(q2)).slice(0, 4);
      setSearchResults({ campaigns: camps, subscribers: subs, lists });
      setSearchLoading(false);
    }, 280);
    return () => clearTimeout(t);
  }, [searchQuery, searchOpen]);

  const totalResults = searchResults.campaigns.length + searchResults.subscribers.length + searchResults.lists.length;

  const handleSearchNav = (path) => {
    setSearchOpen(false);
    navigate(path);
  };

  /* ── Notifications ── */
  const [notifOpen,    setNotifOpen]    = useState(false);
  const [notifItems,   setNotifItems]   = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const prevBadgeRef = useRef(null);

  const playNotifSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [[880, 0], [1108, 0.13]].forEach(([freq, delay]) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = freq;
        const t = ctx.currentTime + delay;
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.18, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
        osc.start(t);
        osc.stop(t + 0.45);
      });
    } catch (_) {}
  }, []);

  // Badge = every real notification except the "all-clear" placeholder
  // Derived directly from items — never out of sync
  const notifBadge = notifItems.filter(i => i.key !== 'ok:all-clear').length;

  // localStorage helpers for system-alert dismissals
  const getSysDismissed = () => new Set(JSON.parse(localStorage.getItem('_ndismiss') || '[]'));
  const addSysDismissed = (key) => {
    const s = getSysDismissed(); s.add(key);
    localStorage.setItem('_ndismiss', JSON.stringify([...s]));
  };

  const fetchNotifications = useCallback(async () => {
    setNotifLoading(true);
    const h = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    const sysDismissed = getSysDismissed();
    const items = [];

    // ── DB notifications (server already filters dismissed ones per user) ──
    const dbRes = await axios.get('/api/notifications', { headers: h }).catch(() => null);
    (dbRes?.data || []).forEach(n => items.push({ ...n, source: 'db' }));

    // ── System-derived alerts ──
    const pushSys = (type, title, body) => {
      const key = `${type}:${title}`;
      if (!sysDismissed.has(key)) items.push({ source: 'sys', key, type, title, body });
    };

    const [quotaRes, campRes, sesRes] = await Promise.allSettled([
      axios.get('/api/settings/ses/quota',        { headers: h }),
      axios.get('/api/campaigns?page=1&limit=10', { headers: h }),
      axios.get('/api/settings/ses-status',        { headers: h }),
    ]);

    if (quotaRes.status === 'fulfilled' && quotaRes.value.data?.configured) {
      const q   = quotaRes.value.data;
      const pct = (q.sentLast24Hours || 0) / (q.max24HourSend || 1);
      if (pct >= 0.9)      pushSys('danger', 'SES quota nearly full',  `${Math.round(pct * 100)}% of your daily quota used. Campaigns may fail.`);
      else if (pct >= 0.7) pushSys('warn',   'SES quota usage high',   `You've used ${Math.round(pct * 100)}% of your daily SES quota.`);
    }

    if (campRes.status === 'fulfilled') {
      const camps = Array.isArray(campRes.value.data) ? campRes.value.data : (campRes.value.data?.data ?? []);
      const sending = camps.filter(c => c.status === 'sending');
      const done    = camps.filter(c => c.status === 'completed').slice(0, 2);
      if (sending.length) pushSys('info', `${sending.length} campaign${sending.length > 1 ? 's' : ''} in progress`, sending.map(c => c.name).join(', '));
      done.forEach(c => pushSys('ok', 'Campaign delivered', `"${c.name}" was sent successfully.`));
    }

    if (sesRes.status === 'fulfilled') {
      const s = sesRes.value.data;
      if (!s?.configured) {
        pushSys('warn', 'SES not connected', 'Connect AWS SES credentials to start sending campaigns.');
      } else {
        // Only show bounce/complaint alerts if enough emails sent to be meaningful (≥10)
        const enoughData = (s.sesAttempts || 0) >= 10;
        if (enoughData && (s.bounceRate    || 0) > 0.05)  pushSys('danger', 'High bounce rate',    `Bounce rate is ${((s.bounceRate||0)*100).toFixed(1)}% — above the 5% threshold.`);
        if (enoughData && (s.complaintRate || 0) > 0.001) pushSys('danger', 'High complaint rate', `Complaint rate is ${((s.complaintRate||0)*100).toFixed(3)}% — above the 0.1% threshold.`);
        if (s.dkim === false)                              pushSys('warn',   'DKIM not enabled',    'Add DKIM CNAME records to your DNS for better inbox placement.');
      }
    }

    // "All clear" placeholder only when truly nothing else to show
    if (!items.length) items.push({ source: 'sys', key: 'ok:all-clear', type: 'ok', title: 'All systems normal', body: 'No alerts — your sender reputation looks healthy.' });

    setNotifItems(items);
    setNotifLoading(false);
  }, []); // eslint-disable-line

  const dismissNotif = useCallback(async (item, idx) => {
    // Optimistic remove — badge auto-recalculates because it's derived from notifItems
    setNotifItems(prev => prev.filter((_, i) => i !== idx));
    // Persist
    if (item.source === 'db') {
      axios.post(`/api/notifications/${item.id}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      }).catch(() => {});
    } else {
      addSysDismissed(item.key);
    }
  }, []); // eslint-disable-line

  // Fetch on mount then poll every 30 s so ticket replies surface promptly
  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, 30000);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  // Play a sound whenever the unread badge count increases after the first load
  useEffect(() => {
    if (prevBadgeRef.current !== null && notifBadge > prevBadgeRef.current) {
      playNotifSound();
    }
    prevBadgeRef.current = notifBadge;
  }, [notifBadge, playNotifSound]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('adminMode');
    navigate('/login');
  };

  const handleNavigate = (key) => {
    navigate(key);
    if (isMobile) setDrawerOpen(false);
  };

  const handleToggleAdminMode = (val) => {
    const next = typeof val === 'boolean' ? val : !adminMode;
    setAdminMode(next);
    localStorage.setItem('adminMode', String(next));
    navigate(next ? '/admin' : '/');
  };

  const activeKey = location.pathname.startsWith('/campaigns/')
    ? '/campaigns'
    : location.pathname.startsWith('/automations')
    ? '/automations'
    : location.pathname.startsWith('/forms')
    ? '/forms'
    : location.pathname.match(/^\/admin\/orgs\/\d+/)
    ? '/admin/orgs'
    : location.pathname;

  const pageTitle = location.pathname.match(/\/campaigns\/\d+\/edit/)
    ? 'Edit Campaign'
    : location.pathname.match(/\/campaigns\/\d+\/report/)
    ? 'Campaign Report'
    : location.pathname.match(/\/admin\/orgs\/\d+/)
    ? 'Organisation Detail'
    : PAGE_TITLES[location.pathname] || 'SES Mailbox';

  const sidebarProps = {
    collapsed, isMobile, activeKey,
    onNavigate:        handleNavigate,
    onCollapse:        () => setCollapsed(c => !c),
    onLogout:          handleLogout,
    adminMode,
    onToggleAdminMode: handleToggleAdminMode,
    isDark,
  };

  const sidebarBg = isDark ? '#1e1b4b' : 'var(--surface)';
  const currentWidth = collapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  return (
    <Layout style={{ minHeight: '100vh', background: contentBg }}>

      {/* Desktop Sider */}
      {!isMobile && (
        <Sider
          collapsed={collapsed}
          collapsedWidth={COLLAPSED_WIDTH}
          width={SIDEBAR_WIDTH}
          trigger={null}
          style={{
            background: sidebarBg,
            borderRight: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid var(--border)',
            position: 'fixed', left: 0, top: 0, bottom: 0, zIndex: 100,
            transition: 'width 0.2s',
          }}
        >
          <SidebarContent {...sidebarProps} />
        </Sider>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          placement="left"
          width={SIDEBAR_WIDTH}
          styles={{
            body:   { padding: 0, background: sidebarBg, height: '100%' },
            header: { display: 'none' },
          }}
          style={{ zIndex: 1001 }}
          closable={false}
        >
          <SidebarContent {...sidebarProps} />
        </Drawer>
      )}

      {/* Main area */}
      <Layout style={{
        marginLeft: isMobile ? 0 : currentWidth,
        transition: 'margin-left 0.2s',
        background: contentBg,
      }}>

        {/* Header */}
        <Header style={{
          background: headerBg,
          padding: '0 20px',
          height: 58,
          lineHeight: '58px',
          borderBottom: `1px solid ${headerBorder}`,
          display: 'flex', alignItems: 'center', gap: 12,
          position: 'sticky', top: 0, zIndex: 99,
          boxShadow: isDark ? 'none' : 'var(--shadow-sm)',
        }}>
          {/* Mobile hamburger */}
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined style={{ fontSize: 17 }} />}
              onClick={() => setDrawerOpen(true)}
              style={{ color: headerTitle, padding: '4px 6px', flexShrink: 0 }}
            />
          )}

          {/* Page title */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? 15 : 18, fontWeight: 650, letterSpacing: '-0.02em',
              color: headerTitle, lineHeight: 1.2 }}>
              {pageTitle}
              {adminMode && (
                <span style={{
                  marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
                  background: 'rgba(220,38,38,0.15)', color: '#ef4444',
                  border: '1px solid rgba(239,68,68,0.3)', letterSpacing: '0.05em', textTransform: 'uppercase',
                  verticalAlign: 'middle',
                }}>
                  Admin
                </span>
              )}
            </div>
          </div>

          {/* Search bar (desktop) */}
          {!isMobile && (
            <div
              onClick={() => setSearchOpen(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '0 12px', height: 34,
                background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-2)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--border)'}`,
                borderRadius: 'var(--r-md)',
                color: isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-3)',
                fontSize: 12.5, width: 260, cursor: 'text', flexShrink: 0,
                transition: 'border-color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--brand-primary)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.1)' : 'var(--border)'}
            >
              <SearchOutlined style={{ fontSize: 13 }} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Search campaigns, contacts…</span>
              <kbd style={{
                fontFamily: 'var(--font-mono)', fontSize: 10, padding: '1px 5px', flexShrink: 0,
                background: isDark ? 'rgba(255,255,255,0.08)' : 'var(--surface)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--border)'}`,
                borderRadius: 4, color: isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-3)',
                lineHeight: '16px', display: 'inline-block',
              }}>⌘K</kbd>
            </div>
          )}

          {/* Bell — notifications popover */}
          <Popover
            open={notifOpen}
            onOpenChange={(v) => {
              setNotifOpen(v);
              if (v) fetchNotifications();
            }}
            trigger="click"
            placement="bottomRight"
            arrow={false}
            content={
              <div style={{ width: 320 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px 8px', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text)' }}>Notifications</span>
                  <Button type="link" size="small" onClick={fetchNotifications} style={{ padding: 0, fontSize: 12 }}>Refresh</Button>
                </div>
                {notifLoading ? (
                  <div style={{ padding: '24px 0', textAlign: 'center' }}><Spin size="small" /></div>
                ) : (
                  <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                    {notifItems.map((item, i) => {
                      const dotColor = {
                        ok:     'var(--ok)',
                        warn:   'oklch(0.55 0.14 75)',
                        danger: 'var(--danger)',
                        info:   'var(--info)',
                      }[item.type] || 'var(--info)';
                      return (
                        <div key={i}
                          onClick={() => { if (item.link) { setNotifOpen(false); navigate(item.link); } }}
                          style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10,
                            padding: '10px 10px 10px 14px',
                            borderBottom: i < notifItems.length - 1 ? '1px solid var(--border)' : 'none',
                            background: i % 2 === 0 ? 'transparent' : 'var(--surface-2)',
                            cursor: item.link ? 'pointer' : 'default',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={e => { if (item.link) e.currentTarget.style.background = 'var(--brand-tint)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'var(--surface-2)'; }}
                        >
                          <div style={{
                            width: 8, height: 8, borderRadius: '50%', background: dotColor,
                            marginTop: 5, flexShrink: 0,
                          }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{item.title}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.4 }}>{item.body}</div>
                          </div>
                          <button
                            onClick={() => dismissNotif(item, i)}
                            title="Dismiss"
                            style={{
                              flexShrink: 0, marginTop: 1,
                              background: 'none', border: 'none', cursor: 'pointer',
                              padding: '2px 4px', borderRadius: 4,
                              color: 'var(--text-3)', fontSize: 14, lineHeight: 1,
                              opacity: 0.6,
                            }}
                            onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'var(--surface-2)'; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.background = 'none'; }}
                          >×</button>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border)', textAlign: 'center' }}>
                  <Button type="link" size="small" onClick={() => { setNotifOpen(false); navigate('/deliverability'); }} style={{ fontSize: 12, color: 'var(--brand-primary)' }}>
                    View Deliverability Dashboard →
                  </Button>
                </div>
              </div>
            }
          >
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <Button
                type="text" size="small"
                icon={<BellOutlined style={{ fontSize: 17 }} />}
                style={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-2)' }}
              />
              {notifBadge > 0 && (
                <span style={{
                  position: 'absolute', top: 3, right: 3,
                  minWidth: 14, height: 14, borderRadius: 7, padding: '0 3px',
                  background: 'var(--danger)', border: `1.5px solid ${headerBg}`,
                  fontSize: 9, fontWeight: 700, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'var(--font-mono)', lineHeight: 1,
                }}>
                  {notifBadge > 9 ? '9+' : notifBadge}
                </span>
              )}
            </div>
          </Popover>

          {/* Expand sidebar button (desktop collapsed) */}
          {!isMobile && collapsed && (
            <Tooltip title="Expand sidebar" placement="bottom">
              <Button
                type="text" size="small"
                icon={<MenuUnfoldOutlined />}
                onClick={() => setCollapsed(false)}
                style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-3)', flexShrink: 0 }}
              />
            </Tooltip>
          )}
        </Header>

        {/* ── Search modal ── */}
        <Modal
          open={searchOpen}
          onCancel={() => setSearchOpen(false)}
          footer={null}
          width={580}
          destroyOnHidden
          closable={false}
          styles={{ body: { padding: 0 } }}
          style={{ top: 120 }}
        >
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <SearchOutlined style={{ fontSize: 15, color: 'var(--text-3)', flexShrink: 0 }} />
            <Input
              ref={searchInputRef}
              placeholder="Search campaigns, subscribers, lists…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              variant="borderless"
              size="large"
              style={{ padding: 0, fontSize: 15 }}
              onKeyDown={e => e.key === 'Escape' && setSearchOpen(false)}
            />
            {searchLoading && <Spin size="small" style={{ flexShrink: 0 }} />}
            <kbd style={{
              flexShrink: 0, fontSize: 11, padding: '1px 6px', borderRadius: 4,
              border: '1px solid var(--border)', background: 'var(--surface-2)',
              color: 'var(--text-3)', fontFamily: 'var(--font-mono)',
            }}>Esc</kbd>
          </div>

          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {!searchQuery.trim() && (
              <div style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
                Start typing to search campaigns, subscribers, and lists
              </div>
            )}

            {searchQuery.trim() && !searchLoading && totalResults === 0 && (
              <div style={{ padding: '32px 16px' }}>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No results for "${searchQuery}"`} />
              </div>
            )}

            {searchResults.campaigns.length > 0 && (
              <div>
                <div style={{ padding: '8px 16px 4px', fontSize: 10.5, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Campaigns</div>
                {searchResults.campaigns.map(c => (
                  <div
                    key={c.id}
                    onClick={() => handleSearchNav(`/campaigns/${c.id}/report`)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '9px 16px', cursor: 'pointer',
                      borderBottom: '1px solid var(--border)',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--brand-tint)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <MailOutlined style={{ color: 'var(--brand-primary)', fontSize: 14, flexShrink: 0 }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                        {c.subject && <div style={{ fontSize: 11.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.subject}</div>}
                      </div>
                    </div>
                    <Tag style={{ fontSize: 10.5, borderRadius: 4, flexShrink: 0, marginLeft: 8 }}>{c.status}</Tag>
                  </div>
                ))}
              </div>
            )}

            {searchResults.lists.length > 0 && (
              <div>
                <div style={{ padding: '8px 16px 4px', fontSize: 10.5, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Lists</div>
                {searchResults.lists.map(l => (
                  <div
                    key={l.id}
                    onClick={() => handleSearchNav('/lists')}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 16px', cursor: 'pointer',
                      borderBottom: '1px solid var(--border)',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--brand-tint)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <TeamOutlined style={{ color: 'var(--brand-primary)', fontSize: 14, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text)' }}>{l.name}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{(l.subscriber_count ?? 0).toLocaleString()} subscribers</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {searchResults.subscribers.length > 0 && (
              <div>
                <div style={{ padding: '8px 16px 4px', fontSize: 10.5, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Subscribers</div>
                {searchResults.subscribers.map(s => {
                  const name = [s.first_name, s.last_name].filter(Boolean).join(' ') || s.name || '';
                  return (
                    <div
                      key={s.id}
                      onClick={() => handleSearchNav('/subscribers')}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '9px 16px', cursor: 'pointer',
                        borderBottom: '1px solid var(--border)',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--brand-tint)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                        background: 'linear-gradient(135deg, var(--brand-primary), #2e6bf5)',
                        color: '#fff', fontSize: 11, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {name ? name[0].toUpperCase() : s.email[0].toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name || s.email}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{s.email}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {searchQuery.trim() && totalResults > 0 && (
            <div style={{ padding: '8px 16px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{totalResults} result{totalResults !== 1 ? 's' : ''} found</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {searchResults.campaigns.length > 0 && <Button size="small" type="link" onClick={() => handleSearchNav('/campaigns')} style={{ fontSize: 11.5 }}>All Campaigns →</Button>}
                {searchResults.subscribers.length > 0 && <Button size="small" type="link" onClick={() => handleSearchNav('/subscribers')} style={{ fontSize: 11.5 }}>All Subscribers →</Button>}
              </div>
            </div>
          )}
        </Modal>

        {/* Content */}
        <Content style={{
          padding: isMobile ? '16px 14px' : '26px 28px',
          background: contentBg,
          minHeight: 'calc(100vh - 58px)',
        }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
